import { useState } from "react";
import { FlaskConical, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

/**
 * Founder-only playground: paste an EN paragraph and watch it flow through
 * the SAME mask → LLM → restore pipeline the article translator uses.
 *
 * Nothing is persisted — the input and output live only in local state.
 */
const SAMPLE = `The Stoic Journal is our editorial home for reflections on Virtue Guide and StoicOs.ai. Some entries also touch on The Agora and the Content Vault. AutoSEO drafts the first pass; the founder edits and publishes.`;

function OutputCard({ testId, label, text, tone = "neutral" }) {
  const border =
    tone === "success"
      ? "border-emerald-400/40"
      : tone === "warn"
      ? "border-red-400/40"
      : "border-white/10";
  return (
    <div className={`border ${border} bg-black/40 p-4`} data-testid={testId}>
      <p className="text-[10px] tracking-[0.28em] uppercase text-gold/85 mb-2">{label}</p>
      <pre className="whitespace-pre-wrap break-words font-reading text-[13.5px] text-bone/90 leading-[1.65]">
        {text || <span className="text-bone/40 italic">empty</span>}
      </pre>
    </div>
  );
}

export default function LexiconTestTranslation() {
  const [text, setText] = useState("");
  const [target, setTarget] = useState("nl");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const run = async () => {
    setErr("");
    setResult(null);
    const trimmed = text.trim();
    if (!trimmed) {
      setErr("Paste an English paragraph to translate.");
      return;
    }
    setBusy(true);
    try {
      const out = await adminApi.testTranslation(trimmed, target);
      setResult(out);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const detected = result?.protected_terms_detected || [];
  const survived = result?.protected_terms_survived || {};
  const allSurvived =
    detected.length === 0 || detected.every((b) => survived[b] === true);

  return (
    <section
      className="border border-gold/20 bg-white/[0.02] p-5 md:p-6 space-y-4"
      data-testid="admin-lexicon-test-panel"
    >
      <header className="flex items-center gap-2">
        <FlaskConical size={16} strokeWidth={1.5} className="text-gold" />
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-gold">Playground</p>
          <h3 className="font-serif text-lg text-bone mt-0.5">Test translation</h3>
          <p className="text-[12.5px] text-bone/60 mt-1 max-w-2xl leading-relaxed">
            Paste an English paragraph to see the full mask → translate → restore
            pipeline. Nothing is saved. Use this whenever you add a new brand to
            the Lexicon to prove it survives translation.
          </p>
        </div>
      </header>

      <div className="grid gap-3 md:grid-cols-[1fr_auto] items-start">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder="Paste English text (up to ~6000 chars)…"
          data-testid="admin-lexicon-test-input"
          className="w-full bg-black border border-white/15 text-bone p-3 text-[13.5px] focus:outline-none focus:border-gold resize-y font-reading"
        />
        <div className="flex md:flex-col gap-2 items-stretch min-w-[160px]">
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="admin-lexicon-test-lang"
            className="bg-black border border-white/15 text-bone px-3 py-2 text-sm focus:outline-none focus:border-gold"
          >
            <option value="nl">EN → NL</option>
            <option value="en">NL → EN</option>
          </select>
          <button
            type="button"
            onClick={() => setText(SAMPLE)}
            data-testid="admin-lexicon-test-sample"
            className="text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/15 px-3 py-2 hover:text-gold hover:border-gold/40"
          >
            Load sample
          </button>
          <button
            type="button"
            onClick={run}
            disabled={busy || !text.trim()}
            data-testid="admin-lexicon-test-run"
            className="inline-flex items-center justify-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-gold border border-gold/60 px-3 py-2 hover:bg-gold/10 disabled:opacity-40"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} strokeWidth={1.75} />}
            {busy ? "Translating…" : "Run"}
          </button>
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-400" data-testid="admin-lexicon-test-err">
          {err}
        </p>
      )}

      {result && (
        <div className="space-y-3" data-testid="admin-lexicon-test-result">
          {/* Verdict */}
          <div
            className={`flex items-center gap-2 border px-3 py-2 ${
              allSurvived
                ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-300"
                : "border-red-400/40 bg-red-400/5 text-red-300"
            }`}
            data-testid="admin-lexicon-test-verdict"
          >
            {allSurvived ? <ShieldCheck size={15} strokeWidth={1.75} /> : <ShieldAlert size={15} strokeWidth={1.75} />}
            <p className="text-[12px] tracking-[0.14em]">
              {detected.length === 0
                ? "No protected terms detected in the input."
                : allSurvived
                ? `All ${detected.length} protected term(s) survived translation.`
                : `${Object.values(survived).filter((v) => !v).length} of ${detected.length} protected term(s) did NOT survive. Investigate below.`}
            </p>
          </div>

          {/* Detected terms */}
          {detected.length > 0 && (
            <div className="border border-white/10 bg-black/40 p-4" data-testid="admin-lexicon-test-detected">
              <p className="text-[10px] tracking-[0.28em] uppercase text-gold/85 mb-2">
                Protected terms detected ({detected.length})
              </p>
              <ul className="flex flex-wrap gap-2">
                {detected.map((brand) => (
                  <li
                    key={brand}
                    data-testid={`admin-lexicon-test-brand-${brand.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                    className={`inline-flex items-center gap-1.5 text-[11px] border px-2 py-1 ${
                      survived[brand]
                        ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/5"
                        : "border-red-400/40 text-red-300 bg-red-400/5"
                    }`}
                  >
                    {survived[brand] ? "✓" : "✕"} {brand}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Stage-by-stage output */}
          <div className="grid gap-3 md:grid-cols-2">
            <OutputCard testId="admin-lexicon-test-original" label="1 · Original (EN)" text={result.original} />
            <OutputCard testId="admin-lexicon-test-masked"   label="2 · Masked (LLM input)" text={result.masked} />
            <OutputCard testId="admin-lexicon-test-translated" label={`3 · Translated (${(result.target_language || "").toUpperCase()}), before restore`} text={result.translated_masked} />
            <OutputCard
              testId="admin-lexicon-test-restored"
              label={`4 · Restored (${(result.target_language || "").toUpperCase()}), final`}
              text={result.restored}
              tone={allSurvived ? "success" : "warn"}
            />
          </div>
        </div>
      )}
    </section>
  );
}
